// Blended per-town score: combines affordability, market timing and commute
// into one 0..100 rating so areas can be ranked holistically. Pure functions.

export const BLEND_WEIGHTS = { affordability: 0.4, timing: 0.25, commute: 0.35 };

export interface BlendInput {
  town: string;
  combinedPt: number | null; // sum of transit minutes across all destinations
  numDest: number; // how many destinations (work(s) + school)
  townMedian: number | null; // HDB median price for the chosen flat type
  buyingPower: number; // HDB buying power
  entryScore: number | null; // flat-type market-timing entry score (0..100)
}

export interface BlendResult {
  town: string;
  blended: number;
  affordability: number; // 0..100 component
  timing: number | null; // null = not used (no signal / not predictive)
  commute: number;
  townMedian: number | null;
  avgPt: number | null; // avg transit minutes per destination
}

const clamp = (x: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, x));

export function blendArea(inp: BlendInput): BlendResult {
  // Affordability: buying power vs town median. 0.7x -> 0, 1.0x -> 60, 1.2x -> 100.
  const affordability =
    inp.townMedian && inp.townMedian > 0
      ? clamp((inp.buyingPower / inp.townMedian - 0.7) * 200)
      : 50; // unknown median -> neutral

  // Timing: the flat type's entry score. Null when there is none or the
  // backtest rule says it isn't predictive — then it carries no weight.
  const timing = inp.entryScore;

  // Commute: average transit minutes per destination. 15min -> 100, ~77min -> 0.
  const avgPt = inp.combinedPt !== null && inp.numDest > 0 ? inp.combinedPt / inp.numDest : null;
  const commute = avgPt === null ? 40 : clamp(100 - (avgPt - 15) * 1.6);

  const wT = timing == null ? 0 : BLEND_WEIGHTS.timing;
  const wSum = BLEND_WEIGHTS.affordability + wT + BLEND_WEIGHTS.commute;
  const blended = Math.round(
    (BLEND_WEIGHTS.affordability * affordability + wT * (timing ?? 0) + BLEND_WEIGHTS.commute * commute) / wSum,
  );

  return { town: inp.town, blended, affordability: Math.round(affordability), timing: timing == null ? null : Math.round(timing), commute: Math.round(commute), townMedian: inp.townMedian, avgPt: avgPt === null ? null : Math.round(avgPt) };
}
